import React, { useEffect, useState } from "react";
import { X, ArrowRight } from "lucide-react";
import { usePersona } from "@/contexts/PersonaContext";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";
import { trackEvent } from "@/lib/analytics";
import { ENDORSEMENT } from "@/lib/brand";
import { useBooking } from "@/contexts/BookingContext";
import { usePersonalization, deriveNeed, PROFILES } from "@/lib/personalization";

const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };
const PERSONA_BY_GOAL = { work: "relocate", retire: "retire", real_estate: null };

// Spain Move Check-in: 3 quick questions shown once, before the hero. Spain-only, no email.
export default function SpainCheckin() {
  const { updatePersona } = usePersona();
  const { section } = useSiteContent();
  const { open: openBooking } = useBooking();
  const { p, nextSteps: liveSteps } = usePersonalization();
  const c = section("spain_checkin", HOME.checkin);
  const steps = [
    { key: "goal", question: c.q1, options: (c.q1_options || []).filter((o) => PROFILES.includes(o.id)) },
    { key: "stage", question: c.q2, options: c.q2_options },
  ];
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [entered, setEntered] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 40);
    trackEvent("checkin_shown");
    document.body.style.overflow = "hidden";
    return () => { clearTimeout(t); document.body.style.overflow = ""; };
  }, []);

  const skip = () => {
    trackEvent("checkin_skipped", { step: step + 1, profile: "neutral" });
    updatePersona({ checkinDone: true, skipped: true, checkin: null, persona: null, status: null });
  };

  const choose = (key, id) => {
    const next = { ...answers, [key]: id };
    setAnswers(next);
    trackEvent("checkin_step", { step: step + 1, answer: id });
    if (step < steps.length - 1) {
      setTimeout(() => setStep(step + 1), 180);
      return;
    }
    const full = { ...next, need: deriveNeed(next.stage) };
    trackEvent("checkin_completed", { profile: next.goal, stage: next.stage });
    updatePersona({ skipped: false, persona: PERSONA_BY_GOAL[next.goal] ?? null, status: next.stage, country: "Spain", checkin: full });
    setDone(true);
  };

  const current = steps[step];

  const go = (action) => {
    trackEvent("checkin_next_step", { action });
    updatePersona({ checkinDone: true });
    const targets = { explore: "#how-we-help", guide: "#guide", how_it_works: "#how-it-works", resources: "/resources" };
    if (action === "call") { openBooking("checkin_next_steps"); return; }
    if (action === "resources") { window.location.assign("/resources"); return; }
    setTimeout(() => document.querySelector(targets[action])?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };

  if (done) {
    return (
      <div data-testid="checkin-next-steps" role="dialog" aria-modal="true" aria-labelledby="checkin-next-title" className="fixed inset-0 z-[100] flex items-center justify-center">
        <img src="/img/home-hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#2B1017]/90" />
        <div className="relative w-full max-w-3xl px-6 md:px-10">
          <p className="text-[#B3A17D] text-[11px] tracking-[0.04em] font-semibold" style={K}><span className="text-white uppercase tracking-[0.3em] text-[10px]">Destino Europe</span> · {ENDORSEMENT}</p>
          <h2 id="checkin-next-title" data-testid="checkin-next-headline" className="mt-5 text-white text-4xl sm:text-5xl leading-[1.05] tracking-tight" style={{ ...K, fontWeight: 600 }}>{p.next_headline}</h2>
          <p className="mt-4 text-white/80 text-base md:text-lg leading-relaxed max-w-xl" style={S}>{p.next_sub}</p>
          <ol className="mt-8 space-y-3">
            {liveSteps.map((st, i) => (
              <li key={i}>
                <button type="button" data-testid={`checkin-next-${st.action}-${i}`} onClick={() => go(st.action)} className="group w-full text-left flex items-start gap-4 px-5 py-4 border border-white/25 bg-white/5 hover:bg-white/10 hover:border-[#B3A17D] transition-colors text-white">
                  <span className="text-[#B3A17D] text-lg leading-none mt-0.5" style={K}>0{i + 1}</span>
                  <span className="flex-1 text-[15px] md:text-base" style={S}>{st.text}</span>
                  <ArrowRight size={16} className="shrink-0 mt-1 text-[#B3A17D] transition-transform group-hover:translate-x-1" />
                </button>
              </li>
            ))}
          </ol>
          <button type="button" data-testid="checkin-continue" onClick={() => updatePersona({ checkinDone: true })} className="mt-7 text-white/70 hover:text-white text-[11px] tracking-[0.2em] uppercase py-2 border-b border-white/30" style={K}>{p.continue_label}</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="spain-checkin" role="dialog" aria-modal="true" aria-labelledby="checkin-title" className={`fixed inset-0 z-[100] flex items-center justify-center transition-opacity duration-500 ${entered ? "opacity-100" : "opacity-0"}`}>
      <img src="/img/home-hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#2B1017]/85" />

      <button type="button" data-testid="checkin-skip" onClick={skip} className="absolute top-5 right-5 md:top-8 md:right-8 inline-flex items-center gap-2 text-white/75 hover:text-white text-[11px] tracking-[0.2em] uppercase px-3 py-2" style={K}>
        {c.skip_label} <X size={14} />
      </button>

      <div className={`relative w-full max-w-3xl px-6 md:px-10 transition-all duration-700 ${entered ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
        <p className="text-[#B3A17D] text-[11px] tracking-[0.04em] font-semibold" style={K}>
          <span className="text-white uppercase tracking-[0.3em] text-[10px]">Destino Europe</span> · {ENDORSEMENT}
        </p>
        <h2 id="checkin-title" data-testid="checkin-headline" className="mt-5 text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight" style={{ ...K, fontWeight: 600 }}>{c.headline}</h2>
        <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed max-w-xl" style={S}>{c.sub}</p>

        <div className="mt-10 flex items-center gap-2" aria-hidden="true">
          {steps.map((_, i) => <span key={i} className={`h-[3px] transition-all duration-300 ${i <= step ? "bg-[#B3A17D] w-10" : "bg-white/25 w-6"}`} />)}
          <span className="ml-3 text-white/60 text-[10px] tracking-[0.2em] uppercase" style={K}>{c.step_label.replace("{n}", step + 1)}</span>
        </div>

        <fieldset data-testid={`checkin-step-${current.key}`} className="mt-6 border-0 p-0 m-0">
          <legend className="text-white text-xl md:text-2xl" style={{ ...K, fontWeight: 500 }}>{current.question}</legend>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(current.options || []).map((o) => {
              const active = answers[current.key] === o.id;
              return (
                <button key={o.id} type="button" data-testid={`checkin-option-${o.id}`} onClick={() => choose(current.key, o.id)} aria-pressed={active}
                  className={`group text-left px-5 py-4 min-h-[56px] border transition-colors flex items-center justify-between gap-3 ${active ? "bg-white text-[#451C25] border-white" : "bg-white/5 text-white border-white/25 hover:bg-white/10 hover:border-[#B3A17D]"}`}
                  style={{ ...K, fontWeight: 500 }}>
                  <span className="text-[15px] md:text-base">{o.label}</span>
                  <ArrowRight size={16} className={`shrink-0 transition-transform group-hover:translate-x-1 ${active ? "text-[#451C25]" : "text-[#B3A17D]"}`} />
                </button>
              );
            })}
          </div>
        </fieldset>

        {step > 0 && (
          <button type="button" data-testid="checkin-back" onClick={() => setStep(step - 1)} className="mt-6 text-white/60 hover:text-white text-[11px] tracking-[0.18em] uppercase py-2" style={K}>{c.back_label}</button>
        )}
      </div>
    </div>
  );
}
